import { useSyncExternalStore } from 'react'

/**
 * Display settings, remembered per device: text size and motion. Applied as attributes on <html>
 * (`data-text-size`, `data-motion`) that the stylesheets read. index.html applies the stored
 * values before the first paint with the same keys, so pages never jump in size on load.
 */
export type TextSize = 'default' | 'large' | 'larger'
export type MotionPreference = 'system' | 'reduce'
export interface DisplayPreferences {
  textSize: TextSize
  motion: MotionPreference
}

export const TEXT_SIZES: { value: TextSize; label: string; scale: string }[] = [
  { value: 'default', label: 'Default', scale: '100%' },
  { value: 'large', label: 'Large', scale: '112.5%' },
  { value: 'larger', label: 'Larger', scale: '125%' },
]
export const MOTION_OPTIONS: { value: MotionPreference; label: string }[] = [
  { value: 'system', label: 'Follow my device' },
  { value: 'reduce', label: 'Reduce motion' },
]

const TEXT_KEY = 'bp_text_size'
const MOTION_KEY = 'bp_motion'
const listeners = new Set<() => void>()

function read(): DisplayPreferences {
  try {
    const t = localStorage.getItem(TEXT_KEY)
    const m = localStorage.getItem(MOTION_KEY)
    return {
      textSize: t === 'large' || t === 'larger' ? t : 'default',
      motion: m === 'reduce' ? 'reduce' : 'system',
    }
  } catch {
    return { textSize: 'default', motion: 'system' }
  }
}

let current: DisplayPreferences = read()

/** Sets or clears the <html> attributes for `prefs`. */
export function applyDisplay(prefs: DisplayPreferences = current) {
  const el = document.documentElement
  if (prefs.textSize === 'default') delete el.dataset.textSize
  else el.dataset.textSize = prefs.textSize
  if (prefs.motion === 'reduce') el.dataset.motion = 'reduce'
  else delete el.dataset.motion
}

function store(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* private mode: the choice lasts until the page closes */
  }
}

export function setDisplay(next: Partial<DisplayPreferences>) {
  current = { ...current, ...next }
  if (next.textSize !== undefined)
    store(TEXT_KEY, next.textSize === 'default' ? null : next.textSize)
  if (next.motion !== undefined) store(MOTION_KEY, next.motion === 'reduce' ? 'reduce' : null)
  applyDisplay(current)
  for (const l of listeners) l()
}

export function useDisplay(): [DisplayPreferences, (next: Partial<DisplayPreferences>) => void] {
  const prefs = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
    () => current,
  )
  return [prefs, setDisplay]
}

/** For scripted motion (smooth scrolling): the site setting, else the device setting. */
export function prefersReducedMotion(): boolean {
  if (current.motion === 'reduce') return true
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}
